/**
 * PandaGifterBadge — the five-tier Top Gifter status chip.
 * Same panda mark across every tier; only the material treatment changes.
 * Tiers (user-approved lifetime coins gifted):
 *   💎 diamond 500K+ · platinum 100K+ · gold 25K+ · silver 5K+ · bronze 1K+
 * Renders nothing when `tier` is falsy.
 */

export const PANDA_TIER_STYLES = {
  bronze: {
    label: "Bronze Panda",
    chip: "bg-gradient-to-br from-amber-700/50 to-amber-950/60 border-amber-600/60 text-amber-400",
  },
  silver: {
    label: "Silver Panda",
    chip: "bg-gradient-to-br from-zinc-400/30 to-zinc-700/40 border-zinc-300/50 text-zinc-200",
  },
  gold: {
    label: "Gold Panda",
    chip: "bg-gradient-to-br from-yellow-500/40 to-amber-700/40 border-yellow-400/70 text-yellow-300",
  },
  platinum: {
    label: "Platinum Panda",
    chip: "bg-gradient-to-br from-slate-100/25 to-sky-200/20 border-slate-200/70 text-slate-100",
  },
  diamond: {
    label: "Diamond Panda",
    chip: "bg-gradient-to-br from-cyan-300/30 to-fuchsia-400/20 border-cyan-300/80 text-cyan-200 shadow-[0_0_8px_rgba(103,232,249,0.45)]",
  },
};

export function PandaGifterBadge({ tier, size = "xs" }) {
  const style = PANDA_TIER_STYLES[tier];
  if (!style) return null;
  const sizing = size === "sm"
    ? "text-[10px] px-1.5 py-0.5 gap-1"
    : "text-[8px] px-1 py-[1px] gap-0.5";
  return (
    <span
      data-testid={`panda-badge-${tier}`}
      title={`${style.label} — top gifter`}
      className={`inline-flex items-center rounded-full border font-semibold uppercase tracking-wide leading-none shrink-0 ${sizing} ${style.chip}`}
    >
      <span aria-hidden="true">🐼</span>
      {tier}
    </span>
  );
}

export const PANDA_TIER_LADDER = [
  { tier: "bronze", threshold: 1_000 },
  { tier: "silver", threshold: 5_000 },
  { tier: "gold", threshold: 25_000 },
  { tier: "platinum", threshold: 100_000 },
  { tier: "diamond", threshold: 500_000 },
];

export function nextPandaTier(lifetime) {
  const total = lifetime || 0;
  for (const step of PANDA_TIER_LADDER) {
    if (total < step.threshold) {
      return { ...step, remaining: step.threshold - total, progress: total / step.threshold };
    }
  }
  return null; // diamond — top of the ladder
}

export function PandaBadgeProgress({ lifetime, className = "" }) {
  const total = lifetime || 0;
  const current = [...PANDA_TIER_LADDER].reverse().find((s) => total >= s.threshold)?.tier || null;
  const next = nextPandaTier(total);
  return (
    <div data-testid="panda-badge-progress" className={`rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg" aria-hidden="true">🐼</span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Panda Gifter Status</div>
            <div className="text-sm text-white flex items-center gap-2">
              {current ? <PandaGifterBadge tier={current} size="sm" /> : <span className="text-zinc-400">No badge yet</span>}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-white">{total.toLocaleString()}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-widest">coins gifted</div>
        </div>
      </div>
      {next ? (
        <div className="mt-2">
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-rose-600 to-amber-400"
              style={{ width: `${Math.min(100, Math.round(next.progress * 100))}%` }}
            />
          </div>
          <div className="mt-1 text-[11px] text-zinc-400" data-testid="panda-progress-remaining">
            {next.remaining.toLocaleString()} coins to <span className="capitalize text-white">{next.tier} Panda</span>
          </div>
        </div>
      ) : (
        <div className="mt-2 text-[11px] text-cyan-300" data-testid="panda-progress-max">
          💎 Diamond Panda — top of the ladder
        </div>
      )}
    </div>
  );
}

export default PandaGifterBadge;
